import CommonModal from 'components/common/CommonModal';
import ScreenLayout from 'components/common/ScreenLayout';
import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {minmax} from 'utils/minmax';
import DefaultText from 'components/common/DefaultText';
import Pagination from 'components/common/Pagination';
import {useNavigate} from 'react-router-dom';
import {useRecoilValue} from 'recoil';
import {userInfoState} from 'utils/Atoms';
import {searchBackOfficeContractor} from 'function/api';
import {convertTotalPage} from 'function/converter';
import ContainerHeader from 'components/common/ContainerHeader';
import {formatPhoneNumber} from 'utils/fomatPhoneNumber';
import {formatBusinessRegisterNumber} from 'function/formatter';
import DefaultInput from 'components/common/DefaultInput';
import DefaultImageView from 'components/common/DefaultImageView';
import ChangeContractorNameModal from 'components/common/ChangeContractorNameModal';
import ChangeBusinessRegisterNumberModal from 'components/common/ChangeBusinessRegisterNumberModal';

const ContractorManagement = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({});
  const navigate = useNavigate();
  const userInfo = useRecoilValue(userInfoState);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [data, setData] = useState<null | any>(null);
  const [selectItem, setSelectItem] = useState<null | any>(null);
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    if (!userInfo?.accessToken) {
      navigate('/');
    }
  }, []);

  useEffect(() => {
    if (userInfo?.accessToken) {
      getContractorList();
    }
  }, [userInfo, currentPage]);

  const getContractorList = () => {
    searchBackOfficeContractor({
      userInfo,
      setErrorModal,
      setIsLoading,
      page: currentPage,
      keyword,
    }).then(result => {
      if (result) {
        setData(result);
        const resTotalPage = convertTotalPage(result.totalCount ?? 0, 10);
        setTotalPage(resTotalPage);
      }
    });
  };

  return (
    <ScreenLayout isLoading={isLoading} errorModal={errorModal}>
      <Container>
        <ContainerHeader
          mainTitle="fieldManagement"
          subTitle="contractorManagement"
        />

        <Inner>
          <DefaultText
            text="시공사 리스트"
            textStyle={{
              fontSize: minmax(25),
              fontWeight: 'bold',
              borderBottom: `3px solid ${colors.primary}`,
              width: 'fit-content',
              marginBottom: minmax(20),
            }}
          />

          <SearchArea>
            <InputWrap>
              <DefaultInput
                value={keyword}
                setValue={(e: any) => setKeyword(e)}
                placeholderText="searchPlaceholder"
                wrapperStyle={{height: '100%'}}
                onKeyPress={(e: any) => {
                  if (e.key === 'Enter') {
                    setCurrentPage(1);
                    getContractorList();
                  }
                }}
              />
              <DefaultImageView
                source="images/common/search.png"
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  height: '100%',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setCurrentPage(1);
                  getContractorList();
                }}
              />
            </InputWrap>
          </SearchArea>

          <ResultArea>
            <ResultHeader>
              <DefaultText
                text=""
                textStyle={{...ResultHeaderTextStyle, flex: 0.4}}
              />
              <DefaultText
                text="contractorName"
                textStyle={ResultHeaderTextStyle}
              />
              <DefaultText
                text="phone"
                textStyle={{...ResultHeaderTextStyle, flex: 1.2}}
              />
              <DefaultText
                text="businessRegistrationNumber"
                textStyle={ResultHeaderTextStyle}
              />
              <DefaultText
                text="allowState"
                textStyle={{...ResultHeaderTextStyle, flex: 0.7}}
              />
            </ResultHeader>

            <ResultList>
              {data?.totalCount ? (
                (data.contractorList ?? []).map(
                  (item: any, index: number) => (
                    <ResultItem key={item.uuid}>
                      <DefaultText
                        text={((currentPage - 1) * 10 + index + 1).toString()}
                        textStyle={{...ResultItemStyle, flex: 0.4}}
                      />
                      <DefaultText
                        text={item.name || '-'}
                        textStyle={{...ResultItemStyle, cursor: 'pointer', textDecoration: 'underline'}}
                        onClick={() => setSelectItem({...item, type: 'changeName'})}
                      />
                      <DefaultText
                        text={
                          item.phone
                            ? formatPhoneNumber({phone: item.phone, locale: item.locale})
                            : '-'
                        }
                        textStyle={{...ResultItemStyle, flex: 1.2}}
                      />
                      <DefaultText
                        text={formatBusinessRegisterNumber(item.businessRegisterNumber)}
                        textStyle={{...ResultItemStyle, cursor: 'pointer', textDecoration: 'underline'}}
                        onClick={() => setSelectItem({...item, type: 'changeBizReg'})}
                      />
                      <DefaultText
                        text={item.allowState || '-'}
                        textStyle={{...ResultItemStyle, flex: 0.7}}
                      />
                    </ResultItem>
                  ),
                )
              ) : (
                <DefaultText
                  text="noDataMsgTongList"
                  textStyle={{
                    color: colors.darkGray,
                    textAlign: 'center',
                    margin: `${minmax(12)} 0`,
                  }}
                />
              )}
            </ResultList>
          </ResultArea>

          {data?.totalCount ? (
            <Pagination
              totalPage={totalPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          ) : null}
        </Inner>
      </Container>

      {selectItem?.type === 'changeName' && (
        <ChangeContractorNameModal
          selectItem={selectItem}
          closeModal={() => setSelectItem(null)}
          setIsLoading={setIsLoading}
          setErrorModal={setErrorModal}
        />
      )}
      {selectItem?.type === 'changeBizReg' && (
        <ChangeBusinessRegisterNumberModal
          selectItem={selectItem}
          entityType="contractors"
          closeModal={() => setSelectItem(null)}
          setIsLoading={setIsLoading}
          setErrorModal={setErrorModal}
        />
      )}
    </ScreenLayout>
  );
};

export default ContractorManagement;

const Container = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  flex-direction: column;
  margin-bottom: ${minmax(50)};
`;

const Inner = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${minmax(20)};
`;

const ResultArea = styled.div`
  margin-bottom: ${minmax(40)};
  border-bottom: 1px solid ${colors.darkGray};
`;

const ResultHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: ${minmax(52)};
  background-color: #f7f7f7;
`;

const ResultList = styled.ul`
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ResultItem = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${minmax(8)} 0;
  min-height: ${minmax(52)};
  border-top: 1px solid ${colors.lightGrey};
  &:last-child {
    border-bottom: none;
  }
`;

const ResultHeaderTextStyle: React.CSSProperties = {
  flex: 1,
  textAlign: 'center',
  color: '#585858',
};

const ResultItemStyle: React.CSSProperties = {
  flex: 1,
  textAlign: 'center',
};

const SearchArea = styled.div`
  width: 100%;
  height: ${minmax(60)};
  margin-top: ${minmax(20)};
  display: flex;
  flex-direction: row;
  column-gap: 2%;
  margin-bottom: ${minmax(20)};
`;

const InputWrap = styled.div`
  width: 100%;
  position: relative;
`;
